/**
 * A screen that throws while rendering must not take the whole application
 * down to a blank page. The boundary shows what happened and offers a way
 * back; it resets when the user navigates to another screen.
 *
 * What it says is written for the person in front of it, who may be a
 * storekeeper or a labourer: what happened, that their work is safe, and the
 * two things they can do about it. The technical detail is folded away behind
 * one link, because support needs it and the user does not — and it is the
 * screen's address and the message, which is what makes a report actionable.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Changing this (e.g. the pathname) clears a caught error. */
  resetKey?: string;
  /** Compact form for a single region such as the notification stack. */
  silent?: boolean;
}

interface State { error: Error | null; showDetail: boolean }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, showDetail: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept in the browser console for support; nothing sensitive is rendered.
    console.error('Screen failed to render', error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, showDetail: false });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.silent) return null;

    const where = typeof window === 'undefined' ? '' : window.location.pathname;
    const detail = `${where} — ${this.state.error.message || 'no message'}`;

    return (
      <div className="flex flex-col items-center justify-center text-center py-16 px-6">
        <AlertTriangle size={40} className="text-critical-500 mb-4" />
        <h2 className="text-xl font-semibold">This screen could not open</h2>
        <p className="text-base muted mt-2 max-w-lg">
          Nothing you saved has been lost. Try opening it again — if it happens a second time,
          use another screen and tell your administrator which one this was.
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => window.location.reload()}
          >
            <RefreshCw size={18} /> Open it again
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-2"
            onClick={() => { window.location.href = '/'; }}
          >
            <Home size={18} /> Go to the home screen
          </button>
        </div>

        <button
          type="button"
          className="mt-6 text-[14px] muted underline"
          onClick={() => this.setState((s) => ({ showDetail: !s.showDetail }))}
        >
          {this.state.showDetail ? 'Hide the detail' : 'Show the detail for support'}
        </button>
        {this.state.showDetail && (
          <p className="mt-2 max-w-lg break-words rounded-md border border-border bg-muted px-3 py-2 text-left text-[13px] font-mono">
            {detail}
          </p>
        )}
      </div>
    );
  }
}
